/**
 * The top-bar Save button (spec.md § Top bar, § UI & design / Save state
 * machine). Slice C renders this; the visual state is driven by the
 * {@link SaveState} the editor (Slice B) reports up through the route.
 *
 *   - clean / saved → "Saved" with a ⌘S hint, accent-tinted (low-weight).
 *   - unsaved / conflict → "Save", solid ink fill (the only weighty control).
 *   - saving → "Saving…", disabled.
 *
 * The label depresses 1px on `:active`. Focus shows the 3px soft-accent halo.
 */

import type { SaveState } from "./padEditorContract";

const BASE_CLASS =
  "inline-flex items-center gap-2 rounded-[9px] px-3.5 py-2 font-mono text-[13px] font-medium outline-none transition-colors focus-visible:ring-3 focus-visible:ring-accent/40 active:translate-y-px disabled:cursor-default";

export function SaveButton({
  state,
  onSave,
}: {
  state: SaveState;
  onSave: () => void;
}): React.JSX.Element {
  const isSaving = state === "saving";
  const isSaved = state === "clean" || state === "saved";

  let label: string;
  let variant: string;
  if (isSaving) {
    label = "Saving…";
    variant = "bg-sunken text-text-2";
  } else if (isSaved) {
    label = "Saved";
    variant = "bg-accent/12 text-accent";
  } else {
    label = "Save";
    variant = "bg-primary text-primary-fg hover:opacity-90";
  }

  return (
    <button
      type="button"
      onClick={onSave}
      disabled={isSaving}
      aria-label="Save pad"
      data-save-state={state}
      className={`${BASE_CLASS} ${variant}`}
    >
      {label}
      {isSaved ? <kbd className="font-mono text-[11px] text-accent/70">⌘S</kbd> : null}
    </button>
  );
}
